import React from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import Button from './Button';
import { IMaskInput } from 'react-imask';
import { TbCircleFilled, TbX } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { isCpfValid } from '../../Utils/Helpers';

const getValidationSchema = (t: any) => Yup.object().shape({
    email: Yup.string()
        .required(t('form.validation.required'))
        .matches(
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            t('form.validation.invalid_email')
        ),
    cpf: Yup.string()
        .length(11, t('form.validation.invalid_document_cpf'))
        .required(t('form.validation.required'))
        .test('is-valid-cpf', t('form.validation.invalid_document_cpf'), (value) => isCpfValid(value || '')),
});

interface FormProfileCheckoutProps {
    onSuccess: CallableFunction;
    onCancel?: CallableFunction;
    title?: string;
    buttonLabel?: string;
}

const FormProfileCheckout = ({
    onSuccess,
    onCancel,
    title,
    buttonLabel,
    }: FormProfileCheckoutProps) => {
        
    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);
    const { orderType, isStoreCounter } = useOrderTypeContext();
    const { user, setUser } = useLoginContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton } = useLayoutContext();

    const initialProfileData = {
        email: user?.email,
        cpf: user?.cpf,
    }

    const handleUpdateProfile = async (params: any) => {
        try {
            setMainLoading(true);
            setMainLoadingTitle(t('profile.form_update_profile.loading_title'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));

            const customerService = new CustomerService(orderType);
            const payload = {
                email: params.email,
                cpf: params.cpf
            }
            const updatedProfile = isStoreCounter
                ? await customerService.updateProfileStoreCounter(payload)
                : await customerService.updateProfile(payload);

            if ((isStoreCounter && updatedProfile.customer && updatedProfile.success) || (!isStoreCounter && updatedProfile.profile)) {
                const userUpdated = isStoreCounter ? updatedProfile.customer : updatedProfile.profile;
                setUser(userUpdated);
                setMainLoading(false);
                onSuccess();
            } else {
                setMainLoadingIcon(false);
                setMainLoadingIconError(true);
                setMainLoadingTitle(t('errors.default_title'));
                setMainLoadingMessage(isStoreCounter ? t('profile.form_update_profile.error_message_store_counter') : t('profile.form_update_profile.error_message'));
                setMainLoadingCloseButton(true);
            }
        } catch (error: any) {
            const errorMessage = error.response.data?.errors?.email;
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('errors.default_title'));
            setMainLoadingMessage(errorMessage ? errorMessage : isStoreCounter ? t('profile.form_update_profile.error_message_store_counter') : t('profile.form_update_profile.error_message'));
            setMainLoadingCloseButton(true);
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };


    return (
        <>
            <Formik
                initialValues={initialProfileData}
                validationSchema={validationSchema}
                validateOnMount={true} // forçando a validação na montagem do formulário
                onSubmit={(values) => handleUpdateProfile(values)}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-6 justify-between h-full p-4 pt-0">

                        {title !== '' &&
                            <div className='text-lg leading-5 pt-2 font-semibold'>
                                {title ? title : t('profile.form_update_profile_checkout.title')}
                            </div>
                        }

                        <div className='w-full flex flex-col gap-4 h-full px-2'>
                            <div className='w-full'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile_checkout.email.label')}
                                    {isFieldRequired(validationSchema, 'email') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <IMaskInput
                                    id="email"
                                    name="email"
                                    autoComplete='on'
                                    mask={/^[a-zA-Z0-9@._+-]*$/}
                                    value={values.email}
                                    onAccept={(value) => setFieldValue('email', value)} // Atualiza o valor no Formik
                                    unmask={false} // true|false|'typed'
                                    placeholder={t('profile.form_update_profile_checkout.email.placeholder')}
                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                />
                                <ErrorMessage name="email" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>

                            <div className='w-full'>
                                <span className='font-semibold text-shopfood-title'>
                                    {t('profile.form_update_profile_checkout.document_cpf.label')}
                                    {isFieldRequired(validationSchema, 'cpf') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                </span>
                                <IMaskInput
                                    id="cpf"
                                    name="cpf"
                                    autoComplete='on'
                                    mask={"000.000.000-00"}
                                    value={values.cpf}
                                    onAccept={(value) => setFieldValue('cpf', value)} // Atualiza o valor no Formik
                                    unmask={true} // true|false|'typed'
                                    placeholder="___.___.___-__"
                                    className="relative block w-full rounded border-0 p-2.5 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                />
                                <ErrorMessage name="cpf" component="div" className='text-xs text-shopfood-error pt-1' />
                            </div>
                        </div>

                        <div className='flex gap-2'>
                            <ButtonPrimary type='submit' className='w-full xs:max-w-sm md:max-w-xs' onClick={() => {}}>{ buttonLabel ? buttonLabel : t('profile.form_update_profile_checkout.button_submit_label') }</ButtonPrimary>
                            {onCancel && <Button rounded onClick={() => onCancel()}><span><TbX size={20} /></span></Button>}
                        </div>

                    </Form>

                )}
            </Formik>
        </>
    )
}

export default FormProfileCheckout;
